/**
 * The assessment workflow and who is allowed to act at each stage.
 *
 *   Self Assessment -> Reporting Manager -> Employee accepts -> HR -> Management
 *
 * The last two are per-quarter: HR picks the chain when the quarter is opened, so a
 * quarter may end at Management, at HR, or at the employee's acceptance. Everything
 * below takes a ReviewChain and defaults to the full five - see FULL_CHAIN.
 *
 * Three deliberate design choices:
 *
 * 1. `status` is the single stored field; the active stage is *derived* from it.
 *    Storing both a status and a currentStage invites the two to disagree, and
 *    when they do there is no way to tell which one lied.
 *
 * 2. The manager stage authorizes on `reportingManagerId`, not on the MANAGER
 *    role. Role-based routing here would let any manager in the company review
 *    any employee - the single most damaging bug this module can have.
 *
 * 3. EMPLOYEE_ACCEPTANCE sits between the manager and HR, not at the end. The
 *    employee is answering their own manager's rating, and that argument belongs
 *    between the two people having it - putting it after Management would mean a
 *    decline reopened a rating HR and Management had already signed off, and both
 *    would have to redo their stage over a disagreement they never saw.
 *
 *    Declining walks the row back to AWAITING_MANAGER, so the manager resubmits;
 *    accepting settles their rating for the quarter and moves it to HR. Nothing
 *    disputed ever reaches HR.
 *
 * Department Head is intentionally absent: it is a read-only oversight role, not
 * a blocking approval, so it never appears in this chain.
 */

import type { Role } from './enums';

export const ASSESSMENT_STAGES = [
  'SELF',
  'MANAGER',
  'EMPLOYEE_ACCEPTANCE',
  'HR',
  'MANAGEMENT',
] as const;
export type AssessmentStage = (typeof ASSESSMENT_STAGES)[number];

/**
 * The stages that actually score KRAs.
 *
 * EMPLOYEE_ACCEPTANCE is a stage of the workflow but not of the scoring: the employee
 * answers yes or no to their manager's rating, they do not supply a third one. Anything
 * iterating stages to read or write a `KraRating` wants this list, not ASSESSMENT_STAGES
 * - which is also why an acceptance row can never appear in the kra_ratings table.
 *
 * Spelled out rather than filtered from ASSESSMENT_STAGES so it stays a literal tuple -
 * `z.enum` and every other tuple-shaped consumer need that. The test that it is exactly
 * ASSESSMENT_STAGES minus the acceptance stage is what keeps the two in step.
 */
export const RATING_STAGES = ['SELF', 'MANAGER', 'HR', 'MANAGEMENT'] as const;
export type RatingStage = (typeof RATING_STAGES)[number];

/**
 * The two stages that score each KRA. HR and Management do not: they read the
 * manager's weighted result and record ONE overall rating for the quarter, in their
 * `StageEntry`. Rating eighteen KRAs a third and fourth time made the review stages a
 * copy of the manager's column - and the API never required it of them anyway.
 *
 * Both sides read this: the SPA to decide which columns to draw, the API to decide
 * which stages must have every KRA rated before they may submit.
 */
export const KRA_RATING_STAGES = ['SELF', 'MANAGER'] as const;
export const ratesKras = (stage: AssessmentStage): boolean =>
  (KRA_RATING_STAGES as readonly AssessmentStage[]).includes(stage);

/**
 * Which of the optional stages a quarter runs. Chosen per quarter, by HR, when the
 * quarter is opened - see `setReviewChain` in cycles.service.ts for when it locks.
 *
 * SELF, MANAGER and EMPLOYEE_ACCEPTANCE are not configurable: somebody has to write the
 * assessment, their own manager has to rate it, and the employee has to answer that
 * rating. HR and Management are, because a quarter can legitimately end at either one.
 *
 * Two booleans rather than a stored list of stages: there is no order to get wrong, no
 * subset to validate, and no way to persist a chain that skips the manager.
 */
export interface ReviewChain {
  hr: boolean;
  management: boolean;
}

/** What every quarter ran before the chain was configurable, and the default for a new one. */
export const FULL_CHAIN: ReviewChain = { hr: true, management: true };

export function chainIncludes(stage: AssessmentStage, chain: ReviewChain): boolean {
  if (stage === 'HR') return chain.hr;
  if (stage === 'MANAGEMENT') return chain.management;
  return true;
}

/** The stages this quarter actually runs, in order. What the tracker draws. */
export function chainStages(chain: ReviewChain = FULL_CHAIN): AssessmentStage[] {
  return ASSESSMENT_STAGES.filter((s) => chainIncludes(s, chain));
}

/** "Self -> Manager -> Your Acceptance -> HR", for a summary line. */
export function chainSummary(chain: ReviewChain = FULL_CHAIN): string {
  return chainStages(chain)
    .map((s) => STAGE_LABELS[s])
    .join(' → ');
}

export const STAGE_LABELS: Record<AssessmentStage, string> = {
  SELF: 'Self Assessment',
  MANAGER: 'Manager Review',
  EMPLOYEE_ACCEPTANCE: 'Your Acceptance',
  HR: 'HR Review',
  MANAGEMENT: 'Management Review',
};

export const STAGE_DESCRIPTIONS: Record<AssessmentStage, string> = {
  SELF: 'Achievements, challenges and evidence',
  MANAGER: 'Rates goals, adds comments and feedback',
  EMPLOYEE_ACCEPTANCE: 'Accept the manager’s rating, or send it back with a reason',
  HR: 'Checks fairness, completeness and compliance',
  MANAGEMENT: 'Final rating, promotion and bonus recommendation',
};

export const ASSESSMENT_STATUSES = [
  'NOT_STARTED',
  'SELF_IN_PROGRESS',
  'AWAITING_MANAGER',
  'AWAITING_EMPLOYEE_ACCEPTANCE',
  'AWAITING_HR',
  'AWAITING_MANAGEMENT',
  'COMPLETED',
] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];

export const ASSESSMENT_STATUS_LABELS: Record<AssessmentStatus, string> = {
  NOT_STARTED: 'Not started',
  SELF_IN_PROGRESS: 'Self assessment in progress',
  AWAITING_MANAGER: 'Awaiting manager review',
  AWAITING_EMPLOYEE_ACCEPTANCE: 'Awaiting your acceptance',
  AWAITING_HR: 'Awaiting HR review',
  AWAITING_MANAGEMENT: 'Awaiting management review',
  COMPLETED: 'Completed',
};

/** The stage currently able to act, or null once the assessment is complete. */
export function stageAwaiting(status: AssessmentStatus): AssessmentStage | null {
  switch (status) {
    case 'NOT_STARTED':
    case 'SELF_IN_PROGRESS':
      return 'SELF';
    case 'AWAITING_MANAGER':
      return 'MANAGER';
    case 'AWAITING_EMPLOYEE_ACCEPTANCE':
      return 'EMPLOYEE_ACCEPTANCE';
    case 'AWAITING_HR':
      return 'HR';
    case 'AWAITING_MANAGEMENT':
      return 'MANAGEMENT';
    case 'COMPLETED':
      return null;
  }
}

/**
 * The status an assessment moves to once `stage` has been submitted.
 *
 * COMPLETED when nothing in this quarter's chain follows, which is what makes the last
 * stage the final one regardless of whether that is Management, HR or the employee's
 * own acceptance.
 */
export function statusAfterStage(
  stage: AssessmentStage,
  chain: ReviewChain = FULL_CHAIN,
): AssessmentStatus {
  switch (nextStage(stage, chain)) {
    case 'MANAGER':
      return 'AWAITING_MANAGER';
    case 'EMPLOYEE_ACCEPTANCE':
      return 'AWAITING_EMPLOYEE_ACCEPTANCE';
    case 'HR':
      return 'AWAITING_HR';
    case 'MANAGEMENT':
      return 'AWAITING_MANAGEMENT';
    // SELF never follows another stage, so this is the null case: the chain is done.
    default:
      return 'COMPLETED';
  }
}

/**
 * Where a declined rating goes back to: the manager, to resubmit.
 *
 * NOT back to SELF. The employee's own answers were locked when they submitted and are
 * not what is in dispute - reopening them would let a decline become a second bite at
 * the self assessment, days after the manager read the first one.
 */
export const STATUS_AFTER_DECLINE: AssessmentStatus = 'AWAITING_MANAGER';

/**
 * The next stage this quarter's chain actually runs, or null at the end of it.
 *
 * Scans forward through the canonical order rather than indexing into the filtered
 * chain, so it stays right for a stage the chain does not contain: a row already
 * sitting at HR when HR is switched off moves on to Management, never back to Self.
 */
export function nextStage(
  stage: AssessmentStage,
  chain: ReviewChain = FULL_CHAIN,
): AssessmentStage | null {
  return (
    ASSESSMENT_STAGES.slice(ASSESSMENT_STAGES.indexOf(stage) + 1).find((s) =>
      chainIncludes(s, chain),
    ) ?? null
  );
}

/** Stages already submitted - used to render the progress tracker. */
export function completedStages(
  status: AssessmentStatus,
  chain: ReviewChain = FULL_CHAIN,
): AssessmentStage[] {
  const active = stageAwaiting(status);
  const stages = chainStages(chain);
  if (active === null) return stages;
  const i = ASSESSMENT_STAGES.indexOf(active);
  return stages.filter((s) => ASSESSMENT_STAGES.indexOf(s) < i);
}

/**
 * Once submitted, the employee's own answers become immutable. This is the
 * "Submission locked" step in the spec and the reason later stages can trust
 * what they are reading.
 */
export function isLockedForEmployee(status: AssessmentStatus): boolean {
  return status !== 'NOT_STARTED' && status !== 'SELF_IN_PROGRESS';
}

/** Minimum role for the two stages that authorize by role rather than by identity. */
const STAGE_MIN_ROLE: Record<'HR' | 'MANAGEMENT', readonly Role[]> = {
  HR: ['HR', 'SUPER_ADMIN'],
  MANAGEMENT: ['SUPER_ADMIN'],
};

export type StageDenialCode =
  | 'ALREADY_COMPLETED'
  | 'STAGE_NOT_ACTIVE'
  | 'NOT_THE_EMPLOYEE'
  | 'NO_REPORTING_MANAGER'
  | 'NOT_THE_REPORTING_MANAGER'
  | 'SELF_REVIEW_NOT_ALLOWED'
  | 'INSUFFICIENT_ROLE';

export type StageAuthorization =
  | { ok: true }
  | { ok: false; code: StageDenialCode; message: string };

export interface StageActionRequest {
  /** Current stored status of the assessment. */
  status: AssessmentStatus;
  /** The stage the caller is attempting to submit. */
  stage: AssessmentStage;
  actorId: string;
  actorRole: Role;
  /** The employee the assessment belongs to. */
  employeeId: string;
  /** That employee's reporting manager, as stored on the employee record. */
  reportingManagerId: string | null;
}

/**
 * The one gate every stage submission passes through.
 *
 * Order matters: the stage-ordering check runs before the identity checks so a
 * caller cannot probe who someone's manager is by watching which error comes
 * back from a stage that is not even open.
 */
export function authorizeStageAction(req: StageActionRequest): StageAuthorization {
  const active = stageAwaiting(req.status);

  if (active === null) {
    return {
      ok: false,
      code: 'ALREADY_COMPLETED',
      message: 'This assessment is already complete.',
    };
  }

  if (active !== req.stage) {
    return {
      ok: false,
      code: 'STAGE_NOT_ACTIVE',
      message: `This assessment is at ${STAGE_LABELS[active]}, so ${STAGE_LABELS[req.stage]} cannot be submitted yet.`,
    };
  }

  switch (req.stage) {
    case 'SELF':
      if (req.actorId !== req.employeeId) {
        return {
          ok: false,
          code: 'NOT_THE_EMPLOYEE',
          message: 'Only the employee can submit their own self assessment.',
        };
      }
      return { ok: true };

    /**
     * The employee's own answer to their manager's rating.
     *
     * Identity, like SELF and MANAGER - and HR is deliberately NOT exempt here, for the
     * same reason a SUPER_ADMIN cannot supply the manager stage. Accepting a rating on
     * somebody's behalf is the one thing that would make this step meaningless.
     */
    case 'EMPLOYEE_ACCEPTANCE':
      if (req.actorId !== req.employeeId) {
        return {
          ok: false,
          code: 'NOT_THE_EMPLOYEE',
          message: 'Only the employee can accept or decline their own rating.',
        };
      }
      return { ok: true };

    case 'MANAGER': {
      if (req.reportingManagerId === null) {
        return {
          ok: false,
          code: 'NO_REPORTING_MANAGER',
          message: 'This employee has no reporting manager assigned. HR must assign one first.',
        };
      }
      if (req.reportingManagerId === req.employeeId) {
        return {
          ok: false,
          code: 'SELF_REVIEW_NOT_ALLOWED',
          message: 'An employee cannot be their own reporting manager.',
        };
      }
      // Identity, not role. A SUPER_ADMIN is deliberately not exempt here: the
      // manager stage records this employee's manager's opinion, and nobody
      // else can supply that. Reassignment is HR's job, not an override.
      if (req.actorId !== req.reportingManagerId) {
        return {
          ok: false,
          code: 'NOT_THE_REPORTING_MANAGER',
          message: 'Only this employee’s reporting manager can submit the manager review.',
        };
      }
      return { ok: true };
    }

    case 'HR':
    case 'MANAGEMENT': {
      const allowed = STAGE_MIN_ROLE[req.stage];
      if (!allowed.includes(req.actorRole)) {
        return {
          ok: false,
          code: 'INSUFFICIENT_ROLE',
          message: `${STAGE_LABELS[req.stage]} requires ${allowed.join(' or ')}.`,
        };
      }
      // Nobody reviews their own appraisal, however senior.
      if (req.actorId === req.employeeId) {
        return {
          ok: false,
          code: 'SELF_REVIEW_NOT_ALLOWED',
          message: 'You cannot review your own assessment.',
        };
      }
      return { ok: true };
    }
  }
}

/**
 * Whether the employee may still edit and submit, combining the workflow lock
 * with the quarter window. Both must hold - an open window does not unlock a
 * submitted assessment, and an unsubmitted draft is still stuck once the window
 * shuts (until HR forces it open).
 */
export function canSubmitSelfAssessment(input: {
  status: AssessmentStatus;
  windowOpen: boolean;
}): { ok: boolean; reason: 'OK' | 'LOCKED' | 'WINDOW_CLOSED' } {
  if (isLockedForEmployee(input.status)) return { ok: false, reason: 'LOCKED' };
  if (!input.windowOpen) return { ok: false, reason: 'WINDOW_CLOSED' };
  return { ok: true, reason: 'OK' };
}

/** Which stage, if any, this user is being asked to act on right now. */
export function pendingActionFor(
  status: AssessmentStatus,
  actor: { id: string; role: Role },
  employee: { id: string; reportingManagerId: string | null },
): AssessmentStage | null {
  const active = stageAwaiting(status);
  if (active === null) return null;
  const auth = authorizeStageAction({
    status,
    stage: active,
    actorId: actor.id,
    actorRole: actor.role,
    employeeId: employee.id,
    reportingManagerId: employee.reportingManagerId,
  });
  return auth.ok ? active : null;
}

/**
 * Who may author the KRA library - KRAs and the groups that weight them.
 *
 * Everyone except an individual contributor. HR and SUPER_ADMIN maintain it as the
 * org-wide library; a manager needs it because they are the one who assigns goals, and
 * making them wait on HR to write a KRA is how a cycle stalls in week one. DEPT_HEAD is
 * included: their read-only rule is about the review chain, where an oversight role must
 * not become an approval gate, not about drafting a stencil.
 *
 * `hasReports` is checked as well as `role`, and that is the important part: manager-ness
 * is a fact about the reporting tree, so somebody holding role EMPLOYEE with six direct
 * reports is a manager here. Gating on `role === 'MANAGER'` alone would lock out the real
 * managers in an org that never bothered changing their role.
 */
export function canManageKraLibrary(actor: { role: Role; hasReports: boolean }): boolean {
  return actor.role !== 'EMPLOYEE' || actor.hasReports;
}

/**
 * Who may change or delete a KRA or group that already exists.
 *
 * Creating is open to every manager; rewriting somebody else's is not. The library is
 * org-wide, so without this one manager tidying up could silently retitle or delete the
 * group another manager assigns from - and because assignment copies, the damage would
 * only show the next time somebody tried to assign it.
 *
 * HR is exempt, as everywhere else: unpicking a mess is their job.
 */
export function canModifyKraRecord(
  actor: { id: string; role: Role },
  record: { createdById: string | null },
): boolean {
  if (actor.role === 'HR' || actor.role === 'SUPER_ADMIN') return true;
  return record.createdById !== null && record.createdById === actor.id;
}
